import { DateUtil } from 'src/app/shared/utils/date-util';
import type { LangMap } from 'src/app/shared/utils/lang';
import { isDevEnv } from 'src/app/util';

import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import type { DocumentData } from '@angular/fire/firestore';

import { KmlService } from './kml.service';

import type {
  FsV1Data,
  FsV1DistanceData,
  FsV2DataCampaign,
  FsV2DataCampaignPart,
  FsV2DataSpot,
  FsV2DataSpotPart,
} from '../models/firestore-data.model';
import { DatareportRepository } from '../repositories/datareport.repository';

@Injectable({
  providedIn: 'root',
})
export class FireStoreService {
  private readonly firestore: Firestore = inject(Firestore);
  private readonly datareportRepository = inject(DatareportRepository);
  private readonly kmlService = inject(KmlService);

  getFirestore(): Firestore {
    return this.firestore;
  }

  makeDataWithDateLabel(
    dateLabel: string[],
    data: Record<string, number>,
  ): number[] {
    const newDataValue: number[] = [];
    // console.log(data);
    const labelKeys = Object.keys(data);
    // console.log(labelKeys, data);
    for (const date of dateLabel) {
      newDataValue.push(labelKeys.indexOf(date) >= 0 ? data[date] : 0);
    }
    return newDataValue;
  }

  makeDataWithDateLabelForDistance(
    dateLabel: string[],
    //data: Record<string, Record<string, number>>,
    data: Record<string, [number, number]>,
  ): number[][] {
    const newDataValue1: number[] = [];
    const newDataValue2: number[] = [];
    const labelKeys = Object.getOwnPropertyNames(data);
    for (const date of dateLabel) {
      newDataValue1.push(labelKeys.indexOf(date) >= 0 ? data[date][0] : 0);
      newDataValue2.push(labelKeys.indexOf(date) >= 0 ? data[date][1] : 0);
    }
    return [newDataValue1, newDataValue2];
  }

  makeDataWithDateLabelForTimeflag(
    dateLabel: string[],
    data: Record<string, number[]>,
  ): number[][] {
    const newDataValue: number[][] = [[], [], [], []];
    const labelKeys = Object.getOwnPropertyNames(data);
    for (const date of dateLabel) {
      for (let idx = 0; idx < 4; idx++) {
        newDataValue[idx].push(
          labelKeys.indexOf(date) >= 0 ? data[date][idx] : 0,
        );
      }
    }

    return newDataValue;
  }

  sortByValue(data: Record<string, number>): Map<string, number> {
    return new Map(
      Object.entries(data).sort((a, b) => {
        // ソート処理
        const genreA = a[1];
        const genreB = b[1];

        let comparison = 0;
        if (genreA > genreB) {
          comparison = 1;
        } else if (genreA < genreB) {
          comparison = -1;
        }
        return comparison * -1;
      }),
    );
  }

  isV1Data(collection: string): boolean {
    return collection.startsWith('v1');
  }

  // V2 collection:
  // - 'v2micampaign' -> FsV2DataCampaign[]
  // - 'v2spots' -> FsV2DataSpot[]
  async getV2Data(
    collection: string,
    mapID: string,
    fromDate: string,
    toDate: string,
  ): Promise<FsV2DataCampaign[] | FsV2DataSpot[]> {
    const res = await this.datareportRepository.fetchByDates(
      collection,
      mapID,
      fromDate,
      toDate,
    );
    return res as FsV2DataCampaign[] | FsV2DataSpot[];
  }

  sumV2CampaignData(
    res: FsV2DataCampaign[],
  ): Record<string, FsV2DataCampaignPart> {
    const newData = {} as Record<string, FsV2DataCampaignPart>;
    for (const data of res) {
      for (const v of data.allarea) {
        const key = `${v.cp}__${v.md}__${v.sc}`;
        if (!newData[key]) {
          newData[key] = {
            cp: v.cp,
            md: v.md,
            sc: v.sc,
            pv: 0,
            ss: 0,
            uu: 0,
          };
        }

        newData[key].pv += v.pv;
        newData[key].ss += v.ss;
        newData[key].uu += v.uu;
      }
    }
    return newData;
  }

  async sumV2SpotData(
    res: FsV2DataSpot[],
    mapID: string,
  ): Promise<{
    allarea: Record<string, FsV2DataSpotPart>;
    inmap: Record<string, FsV2DataSpotPart>;
  }> {
    const newData = { allarea: {}, inmap: {} } as {
      allarea: Record<string, FsV2DataSpotPart>;
      inmap: Record<string, FsV2DataSpotPart>;
    };

    // resには、spot.nameが含まれないため、
    // kml内のlandmarkのidを使ってデータを突合し、landmark.nameを追加する
    const landmarks = await this.kmlService.getLandmarks(mapID);

    for (const data of res) {
      for (const spot of data.allarea) {
        const key = spot.id;
        if (!newData.allarea[key]) {
          // KMLには存在しないが、ログのデータには存在するspot.idがある場合
          const spot_name = landmarks[spot.id]
            ? landmarks[spot.id].name
            : { ja: '', en: '' };

          newData.allarea[key] = {
            id: spot.id,
            uu: 0,
            ss: 0,
            sv: 0,
            name: spot_name,
          };
        }
        newData.allarea[key].uu += spot.uu;
        newData.allarea[key].ss += spot.ss;
        newData.allarea[key].sv += spot.sv;
      }

      if (!data.inmap) {
        continue;
      }
      for (const spot of data.inmap) {
        const key = spot.id;
        if (!newData.inmap[key]) {
          // KMLには存在しないが、ログのデータには存在するspot.idがある場合
          const spot_name = landmarks[spot.id]
            ? landmarks[spot.id].name
            : { ja: '', en: '' };

          newData.inmap[key] = {
            id: spot.id,
            uu: 0,
            ss: 0,
            sv: 0,
            name: spot_name,
          };
        }
        newData.inmap[key].uu += spot.uu;
        newData.inmap[key].ss += spot.ss;
        newData.inmap[key].sv += spot.sv;
      }
    }

    return newData;
  }

  // TODO: こちらのメソッドの単体テストを書く
  // TODO: 処理を分割してください.
  // v1 collection:
  // - 'v1miarea'
  // - 'v1landmark'
  // - 'v1milang'
  // - 'v1model'
  // - 'v1os'
  // - 'v1mireferrer'
  // - 'v1miaccess'
  // - 'v1timeflag'
  // - 'v1distance'
  // この関数では使っていない
  // - 'v1timeflag'

  async getV1Data(
    collection: string,
    mapID: string,
    fromDate: string,
    toDate: string,
    isDaily = false,
  ): Promise<FsV1Data | Record<string, FsV1DistanceData>> {
    const dates = DateUtil.dateRangeWithFormat(fromDate, toDate);
    const res = await this.datareportRepository.fetchByDates(
      collection,
      mapID,
      fromDate,
      toDate,
    );

    // v1distanceのデータは特別な形式であるため、ここで変換する
    if (collection === 'v1distance') {
      return this.convertV1DistanceData(res, fromDate, toDate);
    }

    // dailyの場合は日付ごとに集計する. そうでない場合はキーごとに集計する.
    const sumFunc = isDaily ? this.sumV1DataByDate : this.sumV1Data;
    const ret = this.convV1Data(res, sumFunc);

    // dailyの場合は日付のrange内でもキーにない日付を0で埋める
    if (isDaily) {
      for (const date of dates) {
        if (ret.allarea && !(date in ret.allarea.pv)) {
          ret.allarea.pv[date] =
            ret.allarea.pv_iframe[date] =
            ret.allarea.uu[date] =
            ret.allarea.uu_iframe[date] =
            ret.allarea.ss[date] =
            ret.allarea.ss_iframe[date] =
              0;
        }
        if (ret.inmap && !(date in ret.inmap.pv)) {
          ret.inmap.pv[date] =
            ret.inmap.pv_iframe[date] =
            ret.inmap.uu[date] =
            ret.inmap.uu_iframe[date] =
            ret.inmap.ss[date] =
            ret.inmap.ss_iframe[date] =
              0;
        }
      }
    }
    return ret;
  }

  // v1distanceのデータを変換する
  convertV1DistanceData(
    res: DocumentData[],
    fromDate: string,
    toDate: string,
  ): Record<string, FsV1DistanceData> {
    const newData: Record<string, FsV1DistanceData> = {
      inmap: { SUM: {}, AVG: {}, ss: {}, cl: {}, has_session_count: {} },
    };
    const dates = DateUtil.dateRangeWithFormat(fromDate, toDate);

    // resを、日付のキーを持つオブジェクトに変換する
    const dateMap: Record<string, DocumentData> = {};
    for (const dto of res) {
      if (dto.date) {
        dateMap[dto.date] = dto;
      }
    }

    for (const date of dates) {
      // 初期化
      newData.inmap.SUM[date] = 0;
      newData.inmap.AVG[date] = 0;
      newData.inmap.ss[date] = 0;
      newData.inmap.cl[date] = 0;
      newData.inmap.has_session_count[date] = false;

      const dto = dateMap[date];
      if (!dto) {
        continue; // 日付に該当するデータがない場合はスキップ
      }

      if (dto?.inmap?.SUM) {
        const has_session_count = !!dto.inmap.ss;

        newData.inmap.SUM[date] = dto.inmap.SUM;
        newData.inmap.AVG[date] = dto.inmap.AVG;
        if (has_session_count) {
          newData.inmap.ss[date] = dto.inmap.ss;
          newData.inmap.cl[date] = dto.inmap.cl;
          newData.inmap.has_session_count[date] = true;
        } else {
          newData.inmap.ss[date] = 0;
          newData.inmap.cl[date] = 0;
          newData.inmap.has_session_count[date] = false;
        }
      }
    }

    return newData;
  }

  convV1Data(
    res: DocumentData[],
    sumFunc: (
      kvs: Record<string, number> | number,
      all: Record<string, number>,
      props?: DocumentData,
    ) => Record<string, number>,
  ): FsV1Data {
    const newData = {
      allarea: {
        pv: {},
        pv_iframe: {},
        uu: {},
        uu_iframe: {},
        ss: {},
        ss_iframe: {},
      },
      inmap: {
        pv: {},
        pv_iframe: {},
        uu: {},
        uu_iframe: {},
        ss: {},
        ss_iframe: {},
      },
    };

    for (const d of res) {
      if (d.allarea) {
        const allarea = d.allarea;
        if (allarea.pv.all) {
          sumFunc(allarea.pv.all, newData.allarea.pv, d);
        }
        if (allarea.pv.iframe) {
          sumFunc(allarea.pv.iframe, newData.allarea.pv_iframe, d);
        }
        if (allarea.uu.all) {
          sumFunc(allarea.uu.all, newData.allarea.uu, d);
        }
        if (allarea.uu.iframe) {
          sumFunc(allarea.uu.iframe, newData.allarea.uu_iframe, d);
        }
        if (allarea.ss && allarea.ss.all) {
          sumFunc(allarea.ss.all, newData.allarea.ss, d);
        }
        if (allarea.ss && allarea.ss.iframe) {
          sumFunc(allarea.ss.iframe, newData.allarea.ss_iframe, d);
        }
      }
      if (d.inmap) {
        const inmap = d.inmap;
        if (inmap.pv.all) {
          sumFunc(inmap.pv.all, newData.inmap.pv, d);
        }
        if (inmap.pv.iframe) {
          sumFunc(inmap.pv.iframe, newData.inmap.pv_iframe, d);
        }
        if (inmap.uu.all) {
          sumFunc(inmap.uu.all, newData.inmap.uu, d);
        }
        if (inmap.uu.iframe) {
          sumFunc(inmap.uu.iframe, newData.inmap.uu_iframe, d);
        }
        if (inmap.ss && inmap.ss.all) {
          sumFunc(inmap.ss.all, newData.inmap.ss, d);
        }
        if (inmap.ss && inmap.ss.iframe) {
          sumFunc(inmap.ss.iframe, newData.inmap.ss_iframe, d);
        }
      }
    }
    return newData;
  }

  sumV1Data(
    kvs: Record<string, number> | number,
    all: Record<string, number>,
  ): Record<string, number> {
    if (typeof kvs === 'number') {
      // numberの場合は何もしない（このメソッドはオブジェクト用）
      return all;
    }

    for (const key in kvs) {
      if (isDevEnv(key) || key === '0' || key === 'nan' || key === 'None') {
        continue;
      }
      all[key] = +all[key] ? all[key] + kvs[key] : kvs[key];
    }
    return all;
  }

  sumV1DataByDate(
    kvs: Record<string, number> | number,
    all: Record<string, number>,
    props?: DocumentData,
  ): Record<string, number> {
    const date = props?.['date'];
    if (typeof kvs === 'number') {
      if (date) {
        all[date] = +all[date] ? all[date] + kvs : kvs;
      }
    } else {
      for (const key in kvs) {
        if (isDevEnv(key) || key === '0' || key === 'nan' || key === 'None') {
          continue;
        }
        if (date) {
          all[date] = +all[date] ? all[date] + kvs[key] : kvs[key];
        }
      }
    }
    return all;
  }
}

// Re-export types to maintain backward compatibility
export type {
  FsV1DistanceData,
  FsV2DataSpotPart,
} from '../models/firestore-data.model';
